<script>
  // Карточка «Устройства и вход» в общих настройках (этап 16): список устройств с
  // отзывом, включение Face ID на этом устройстве, приглашение нового устройства по QR.
  import { onMount, tick } from 'svelte';
  import qrcode from 'qrcode-generator';
  import { api } from '../api.js';
  import { classifyChatError, showFlash, userStore } from '../stores.js';
  import { isPasskeySupported, isPlatformAuthenticatorAvailable, isUserCancel, registerPasskey } from '../passkey.js';
  import { TEXT } from '../ui-text.js';
  import ActionSheet from './ActionSheet.svelte';

  // setup-passkey=1 в адресе ставит приём приглашения: новое устройство сразу
  // предлагает включить Face ID.
  export let promptPasskeySetup = false;

  let devices = [];
  let passkeys = [];
  let loadError = '';
  let passkeySupported = isPasskeySupported();
  let passkeyBusy = false;
  let passkeyMessage = '';
  let invite = null; // { code, expiresAt, joinUrl, svg }
  let inviteBusy = false;
  let inviteMessage = '';
  let inviteForUser = '';
  let showInviteForOther = false;
  let revokeTarget = null;
  let passkeyTarget = null;

  $: currentDevice = devices.find((d) => d.isCurrent) || null;
  $: currentHasPasskey = Boolean(currentDevice?.hasPasskey);
  $: revokeActions = revokeTarget
    ? [{
        id: 'revoke',
        label: TEXT.auth.deviceRevoke,
        icon: 'trash',
        danger: true,
        confirm: revokeTarget.isCurrent ? TEXT.auth.deviceRevokeCurrentConfirm : TEXT.auth.deviceRevokeConfirm,
      }]
    : [];

  $: passkeyActions = passkeyTarget
    ? [{
        id: 'delete-passkey',
        label: TEXT.auth.passkeyDelete,
        icon: 'trash',
        danger: true,
        confirm: TEXT.auth.passkeyDeleteConfirm,
      }]
    : [];

  async function loadDevices() {
    loadError = '';
    try {
      const result = await api.listDevices();
      devices = result?.items || [];
      passkeys = result?.passkeys || [];
    } catch (e) {
      loadError = classifyChatError(e, TEXT.auth.deviceRevokeFailed);
    }
  }

  function formatWhen(iso) {
    if (!iso) {
      return '';
    }
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  function deviceTitle(device) {
    return device.label || (device.authMethod === 'passkey' ? 'Face ID' : device.authMethod === 'invite' ? 'По приглашению' : 'Сертификат');
  }

  async function enablePasskey() {
    passkeyBusy = true;
    passkeyMessage = '';
    try {
      await registerPasskey('');
      passkeyMessage = TEXT.auth.passkeyEnabled;
      promptPasskeySetup = false;
      await loadDevices();
    } catch (e) {
      console.warn('passkey register failed', e);
      passkeyMessage = isUserCancel(e) ? TEXT.auth.passkeyCancelled : classifyChatError(e, TEXT.auth.passkeyFailed);
    } finally {
      passkeyBusy = false;
    }
  }

  function inviteErrorText(error) {
    if (error?.status === 429) return TEXT.auth.inviteLimit;
    if (error?.status === 403) return TEXT.auth.inviteForbidden;
    if (error?.status === 404) return TEXT.auth.inviteUnknownUser;
    return classifyChatError(error, TEXT.auth.inviteFailed);
  }

  function renderQr(url) {
    const qr = qrcode(0, 'M');
    qr.addData(url);
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  }

  async function createInvite(forUser = null) {
    inviteBusy = true;
    inviteMessage = '';
    try {
      const result = await api.createDeviceInvite(forUser);
      invite = { ...result, svg: renderQr(result.joinUrl) };
    } catch (e) {
      console.warn('invite failed', e);
      inviteMessage = inviteErrorText(e);
    } finally {
      inviteBusy = false;
    }
  }

  async function handleRevokeSelect() {
    const target = revokeTarget;
    revokeTarget = null;
    if (!target) {
      return;
    }
    try {
      await api.revokeDevice(target.deviceId);
      if (target.isCurrent) {
        // Отозвали себя — это выход: страница перезагрузится в экран входа.
        window.location.reload();
        return;
      }
      showFlash(TEXT.auth.deviceRevoked, 'info');
      await loadDevices();
    } catch (e) {
      showFlash(classifyChatError(e, TEXT.auth.deviceRevokeFailed), 'error');
    }
  }

  async function handlePasskeyDeleteSelect() {
    const target = passkeyTarget;
    passkeyTarget = null;
    if (!target?.deviceId) {
      return;
    }
    try {
      await api.deleteDevicePasskey(target.deviceId);
      showFlash(TEXT.auth.passkeyDeleted, 'info');
      await loadDevices();
    } catch (e) {
      showFlash(classifyChatError(e, TEXT.auth.passkeyDeleteFailed), 'error');
    }
  }

  function passkeyDeviceTitle(key) {
    const device = devices.find((d) => d.deviceId === key.deviceId);
    return device ? deviceTitle(device) : (key.label || 'Face ID');
  }

  // К3 (1.27.2): после приглашения карточка — шестая на экране; без прокрутки к ней
  // новый человек видел «Мой профиль» и не знал, что надо включить Face ID.
  let cardElement = null;

  onMount(async () => {
    passkeySupported = await isPlatformAuthenticatorAvailable();
    await loadDevices();
    if (promptPasskeySetup) {
      await tick();
      cardElement?.scrollIntoView?.({ block: 'start' });
    }
  });
</script>

<section class="card" data-testid="devices-card" bind:this={cardElement}>
  <h2>{TEXT.auth.devicesTitle}</h2>
  <p class="hint">{TEXT.auth.devicesLead}</p>

  {#if promptPasskeySetup && passkeySupported && !currentHasPasskey}
    <p class="setup-prompt" role="status">{TEXT.auth.passkeySetupPrompt}</p>
  {/if}

  <div class="actions">
    {#if passkeySupported}
      {#if currentHasPasskey}
        <p class="hint ok">{TEXT.auth.passkeyEnabled}</p>
      {:else}
        <button type="button" class="btn-primary" disabled={passkeyBusy} on:click={enablePasskey}>{TEXT.auth.passkeyEnable}</button>
      {/if}
    {:else}
      <p class="hint">{TEXT.auth.passkeyUnsupported}</p>
    {/if}
  </div>
  {#if passkeyMessage}
    <p class="hint" role="status">{passkeyMessage}</p>
  {/if}
  <p class="hint">{TEXT.auth.passkeyHint}</p>

  {#if loadError}
    <p class="hint error">{loadError}</p>
  {:else if devices.length === 0}
    <p class="hint">{TEXT.auth.devicesEmpty}</p>
  {:else}
    <ul class="devices" aria-label={TEXT.auth.devicesTitle}>
      {#each devices as device (device.deviceId)}
        <li class="device" class:current={device.isCurrent}>
          <div class="device-main">
            <span class="device-title">{deviceTitle(device)}</span>
            <span class="device-meta">
              {formatWhen(device.lastSeenAt || device.createdAt)}
              {#if device.hasPasskey} · {TEXT.auth.deviceHasPasskey}{/if}
              {#if device.isCurrent} · {TEXT.auth.deviceCurrent}{/if}
            </span>
          </div>
          <button type="button" class="btn-secondary small" on:click={() => (revokeTarget = device)}>{TEXT.auth.deviceRevoke}</button>
        </li>
      {/each}
    </ul>
  {/if}

  {#if passkeys.length > 0}
    <h3>{TEXT.auth.passkeysTitle}</h3>
    <ul class="devices" aria-label={TEXT.auth.passkeysTitle}>
      {#each passkeys as key (key.credentialId)}
        <li class="device">
          <div class="device-main">
            <span class="device-title">{TEXT.auth.passkeyItemTitle}</span>
            <span class="device-meta">
              {TEXT.auth.passkeyForDevice} «{passkeyDeviceTitle(key)}» · {formatWhen(key.lastUsedAt || key.createdAt)}
              {#if !key.deviceActive} · {TEXT.auth.passkeyDeviceSignedOut}{/if}
            </span>
          </div>
          <button type="button" class="btn-secondary small" on:click={() => (passkeyTarget = key)}>{TEXT.auth.passkeyDelete}</button>
        </li>
      {/each}
    </ul>
  {/if}

  <h3>{TEXT.auth.inviteTitle}</h3>
  <p class="hint">{TEXT.auth.inviteLead}</p>
  {#if invite}
    <div class="qr" data-testid="invite-qr">{@html invite.svg}</div>
    <p class="hint">{TEXT.auth.inviteManual}</p>
    <p class="code" data-testid="invite-code">{invite.code}</p>
    <p class="hint">{TEXT.auth.inviteExpires} {formatWhen(invite.expiresAt)}</p>
  {/if}
  <div class="actions">
    <button type="button" class="btn-secondary" disabled={inviteBusy} on:click={() => createInvite(null)}>{TEXT.auth.inviteCreate}</button>
    <button type="button" class="btn-secondary" on:click={() => (showInviteForOther = !showInviteForOther)}>{TEXT.auth.inviteCreateFor}</button>
  </div>
  {#if showInviteForOther}
    <div class="actions">
      <input type="text" bind:value={inviteForUser} placeholder={TEXT.auth.inviteUserPlaceholder} aria-label={TEXT.auth.inviteUserPlaceholder} />
      <button type="button" class="btn-secondary" disabled={inviteBusy || !inviteForUser.trim()} on:click={() => createInvite(inviteForUser.trim())}>{TEXT.auth.inviteCreate}</button>
    </div>
  {/if}
  {#if inviteMessage}
    <p class="hint error" role="status">{inviteMessage}</p>
  {/if}
</section>

<ActionSheet
  open={revokeTarget !== null}
  title={revokeTarget ? deviceTitle(revokeTarget) : ''}
  actions={revokeActions}
  on:select={handleRevokeSelect}
  on:close={() => (revokeTarget = null)}
/>

<ActionSheet
  open={passkeyTarget !== null}
  title={passkeyTarget ? `${TEXT.auth.passkeyItemTitle} · ${passkeyDeviceTitle(passkeyTarget)}` : ''}
  actions={passkeyActions}
  on:select={handlePasskeyDeleteSelect}
  on:close={() => (passkeyTarget = null)}
/>

<style>
  .card {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    min-width: 0;
    padding: var(--spacing-lg);
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 16px;
    box-shadow: var(--shadow-sm);
  }

  h3 {
    margin-top: var(--spacing-sm);
    font-size: 1rem;
  }

  .hint {
    color: var(--text-secondary);
    font-size: 0.9rem;
    margin: 0;
  }

  .hint.ok {
    color: var(--text-primary);
  }

  .hint.error {
    color: var(--error-text, var(--text-primary));
  }

  .setup-prompt {
    padding: 0.6rem 0.8rem;
    border-radius: 10px;
    background: color-mix(in srgb, var(--accent-color) 14%, transparent);
    margin: 0;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;
  }

  .actions input {
    flex: 1;
    min-width: 10rem;
    font-size: max(16px, 1rem);
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: var(--bg-primary);
    color: var(--text-primary);
  }

  .devices {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .device {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.6rem 0.8rem;
    border: 1px solid var(--border-color);
    border-radius: 10px;
  }

  .device.current {
    border-color: var(--accent-color);
  }

  .device-main {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .device-title {
    font-weight: 600;
  }

  .device-meta {
    color: var(--text-secondary);
    font-size: 0.85rem;
  }

  .qr {
    width: min(70vw, 260px);
    margin: 0 auto;
    padding: 8px;
    background: #fff;
    border-radius: 12px;
  }

  .qr :global(svg) {
    display: block;
    width: 100%;
    height: auto;
  }

  .code {
    font-family: var(--font-mono);
    font-size: 1.6rem;
    letter-spacing: 0.25em;
    text-align: center;
    margin: 0;
    user-select: all;
  }

  button {
    padding: 0.55rem 1rem;
    border-radius: 10px;
    border: 1px solid var(--border-color);
    background: var(--bg-tertiary);
    color: var(--text-primary);
    font-size: 0.95rem;
  }

  button:disabled {
    opacity: 0.6;
  }

  .btn-primary {
    background: var(--accent-color);
    color: #fff;
    border-color: transparent;
  }

  .small {
    padding: 0.35rem 0.7rem;
    font-size: 0.85rem;
  }
</style>
